import ErrorMessageHandler from 'adaptors/errorHanders/ErrorMessageHandler';
import { ATGJSONResponse } from 'types/JSONResponseType';
import { IsObjectEmpty } from 'utilities/util';

/**
 * A Generic type used for returning the result of object check
 */
type ParseResult<T> =
  | { parsed: T; hasError: false; error?: undefined }
  | { parsed?: undefined; hasError: true; error?: unknown };

/**
 * A simple check for finding the expected field in the object
 *
 * The ATGJSONResponse is generic and can be any other type
 *
 *  for example
 * interface NoneATGResponse {
 *   x: string;
 *  }
 *  function checkNoneATGType(obj:any): obj is NoneATGResponse {
 *   return "x" in obj;
 * }
 *
 * @param obj API Response object
 * @returns results of if expected field is in the object
 */
function checkATGType(obj: ATGJSONResponse): obj is ATGJSONResponse {
  return 'formError' in obj;
}

/**
 * The type guard is passed to check if the object has the referred expected fields
 *
 * @param typeGuard
 * @returns returns a object with the results of the type guard as well as the referred type
 *
 */
const checkExpectedObjectExists =
  <T>(typeGuard: (obj: any) => obj is T) =>
  (text: T): ParseResult<T> => {
    try {
      const parsed = text;
      return typeGuard(parsed) ? { parsed, hasError: false } : { hasError: true };
    } catch (error) {
      return { hasError: true, error };
    }
  };

const parseATGAPIResponse = (atgAPIResponse: { parsed: any; hasError?: false; error?: undefined }) =>
  ErrorMessageHandler(atgAPIResponse.parsed?.formExceptions?.[0].errorCode);

/**
 * The idea behind this adaptor is to make it generic enough to determine if the response is from ATG or not
 *
 * the IsObjectEmpty will determine if we get a empty json object
 *
 * Example of none ATG response
 * const addToBasketNoneATGAPIAdaptor = checkExpectedObjectExists(checkNoneATGType)(data as NoneATGResponse);
 *
 * @param data API Response
 * @returns A custom string
 */
const AddToBasketAPIAdaptor = (data: object) => {
  const parsedObject = IsObjectEmpty(data);
  const addToBasketATGAPIAdaptor = checkExpectedObjectExists(checkATGType)(data as ATGJSONResponse);

  let parsedResponse = '';
  if (!parsedObject && !addToBasketATGAPIAdaptor.hasError) {
    parsedResponse = parseATGAPIResponse(addToBasketATGAPIAdaptor);
  }
  return parsedResponse;
};

export default AddToBasketAPIAdaptor;
